/**
 * AnalyticsDashboard — main Creative Analytics Dashboard page (Phase 28).
 * Route: /dashboard
 * Wraps in DashboardProvider. Shows GlobalFilterBar + 5-tab layout.
 * Tab value controlled by activeTab from useDashboardContext().
 */

// TODO(phase-29): Wire EdgeStateHandler into each tab section.
// EdgeStateHandler exists at @/components/dashboard/shared/EdgeStateHandler.tsx
// but is not yet integrated. Each tab currently handles its own loading/empty
// states inline. A unified edge state wrapper was considered for Phase 28 but
// deferred to avoid scope creep — the inline handling is functional.

import * as Tabs from '@radix-ui/react-tabs';
import {
  BarChart3,
  Shield,
  Lightbulb,
  LayoutList,
  FileSpreadsheet,
  Download,
} from 'lucide-react';
import { useState } from 'react';

import { VerdictChangesStrip } from '@/components/analytics/VerdictChangesStrip';
import { CreativesTable } from '@/components/dashboard/creatives/CreativesTable';
import { GateBars } from '@/components/dashboard/gates/GateBars';
import { SegmentHeatmap } from '@/components/dashboard/gates/SegmentHeatmap';
import { GlobalFilterBar } from '@/components/dashboard/global/GlobalFilterBar';
import { CustomExplorer } from '@/components/dashboard/insights/CustomExplorer';
import { InsightsNav } from '@/components/dashboard/insights/InsightsNav';
import { KpiDrivers } from '@/components/dashboard/insights/KpiDrivers';
import { PortfolioComposition } from '@/components/dashboard/insights/PortfolioComposition';
import { TemplateCalibration } from '@/components/dashboard/insights/TemplateCalibration';
import { ReportBuilder } from '@/components/dashboard/reports/ReportBuilder';
import { ReportList } from '@/components/dashboard/reports/ReportList';
import { downloadMultiSectionCsv } from '@/components/dashboard/shared/CsvExport';
import { EmptyState } from '@/components/dashboard/shared/EmptyState';
import { SectionErrorBoundary } from '@/components/dashboard/shared/SectionErrorBoundary';
import { VerdictsSkeleton } from '@/components/dashboard/shared/SkeletonLoaders';
import { KeyMovements } from '@/components/dashboard/verdicts/KeyMovements';
import { KpiSummaryRow } from '@/components/dashboard/verdicts/KpiSummaryRow';
import { ProjectComparison } from '@/components/dashboard/verdicts/ProjectComparison';
import { TopBottomPerformers } from '@/components/dashboard/verdicts/TopBottomPerformers';
import { VerdictByPlacement } from '@/components/dashboard/verdicts/VerdictByPlacement';
import { VerdictCards } from '@/components/dashboard/verdicts/VerdictCards';
import { VerdictTrendChart } from '@/components/dashboard/verdicts/VerdictTrendChart';
import { Button } from '@/components/ui/Button';
import { useVerdictSummary, useGateSummary } from '@/hooks/useDashboard';
import type { DashboardTab, InsightsSubView } from '@/types/dashboard';

import { DashboardProvider, useDashboardContext } from './DashboardContext';

// ============================================================================
// TAB DEFINITIONS
// ============================================================================

interface TabDef {
  id: DashboardTab;
  label: string;
  icon: React.ReactNode;
}

const TABS: TabDef[] = [
  { id: 'verdicts', label: 'Verdict Distribution', icon: <BarChart3 className="w-4 h-4" /> },
  { id: 'gates', label: 'Quality Gates', icon: <Shield className="w-4 h-4" /> },
  { id: 'insights', label: 'Insights', icon: <Lightbulb className="w-4 h-4" /> },
  { id: 'creatives', label: 'Creatives', icon: <LayoutList className="w-4 h-4" /> },
  { id: 'reports', label: 'Reports', icon: <FileSpreadsheet className="w-4 h-4" /> },
];

// ============================================================================
// INNER DASHBOARD (uses context)
// ============================================================================

function DashboardInner() {
  const { filters, setTab } = useDashboardContext();
  // Pre-fetch verdict data so it's cached when VerdictDistributionContent renders
  const { data: verdictData } = useVerdictSummary(filters);

  // GAP-10: dataTimestamp is now included directly in the data payload
  const apiTimestamp = verdictData?.dataTimestamp;

  return (
    <div className="space-y-0">
      {/* Global Filter Bar */}
      <GlobalFilterBar apiTimestamp={apiTimestamp} />

      {/* Tab Container */}
      <Tabs.Root
        value={filters.activeTab}
        onValueChange={(value) => setTab(value as DashboardTab)}
      >
        {/* Tab List */}
        <Tabs.List className="flex gap-0 border-b border-border-subtle bg-surface px-6">
          {TABS.map((tab) => (
            <Tabs.Trigger
              key={tab.id}
              value={tab.id}
              className="inline-flex items-center gap-1.5 px-4 py-3 text-sm text-secondary
                hover:text-primary border-b-2 border-transparent -mb-px transition-colors
                data-[state=active]:text-brand data-[state=active]:border-brand data-[state=active]:font-medium"
            >
              {tab.icon}
              {tab.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        {/* Tab Content — forceMount keeps state alive; hidden class prevents scroll-triggered tab switches */}
        <div className="px-6 py-4">
          {/* Verdicts Tab */}
          <Tabs.Content value="verdicts" forceMount className={filters.activeTab !== 'verdicts' ? 'hidden' : ''}>
            <SectionErrorBoundary key="verdicts" name="Verdicts">
              <VerdictDistributionContent />
            </SectionErrorBoundary>
          </Tabs.Content>

          {/* Gates Tab */}
          <Tabs.Content value="gates" forceMount className={filters.activeTab !== 'gates' ? 'hidden' : ''}>
            <SectionErrorBoundary key="gates" name="Gates">
              <QualityGatesContent />
            </SectionErrorBoundary>
          </Tabs.Content>

          {/* Insights Tab */}
          <Tabs.Content value="insights" forceMount className={filters.activeTab !== 'insights' ? 'hidden' : 'space-y-4'}>
            <SectionErrorBoundary key="insights" name="Insights">
              <InsightsContent />
            </SectionErrorBoundary>
          </Tabs.Content>

          {/* Creatives Tab */}
          <Tabs.Content value="creatives" forceMount className={filters.activeTab !== 'creatives' ? 'hidden' : 'space-y-4'}>
            <SectionErrorBoundary key="creatives" name="Creatives">
              <CreativesTable />
            </SectionErrorBoundary>
          </Tabs.Content>

          {/* Reports Tab */}
          <Tabs.Content value="reports" forceMount className={filters.activeTab !== 'reports' ? 'hidden' : 'space-y-4'}>
            <SectionErrorBoundary key="reports" name="Reports">
              <ReportsContent />
            </SectionErrorBoundary>
          </Tabs.Content>
        </div>
      </Tabs.Root>
    </div>
  );
}

// ============================================================================
// VERDICT DISTRIBUTION CONTENT
// ============================================================================

function VerdictDistributionContent() {
  const { filters } = useDashboardContext();
  const { data, isLoading, error } = useVerdictSummary(filters);

  if (isLoading) return <VerdictsSkeleton />;

  if (error) {
    return (
      <div className="rounded-xl bg-danger/5 border border-danger/20 px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-danger">Failed to load verdict distribution</div>
          <div className="text-xs text-danger/80 mt-0.5">Please try again or contact support.</div>
        </div>
      </div>
    );
  }

  if (!data?.verdictCards || data.verdictCards.length === 0) {
    return <EmptyState tab="verdicts" />;
  }

  function handleExportAll() {
    if (!data) return;
    const sections: Parameters<typeof downloadMultiSectionCsv>[0] = [];

    // Verdict cards
    sections.push({
      title: 'Verdict Distribution',
      headers: ['Verdict', 'Count', 'Percentage', 'Trend'],
      rows: data.verdictCards.map((c) => [c.verdict, c.count, c.percentage, c.trendDelta ?? 0]),
    });

    // Trend data
    if (data.trendData?.length) {
      sections.push({
        title: 'Verdict Trend',
        headers: ['Date', 'Scale', 'Fix', 'Kill'],
        rows: data.trendData.map((t) => [t.date, t.scale, t.fix, t.kill]),
      });
    }

    // Project comparison
    if (data.projectComparison?.length) {
      sections.push({
        title: 'Project Comparison',
        headers: ['Project', 'Scale', 'Fix', 'Kill', 'Primary KPI', 'KPI Value', 'KPI Trend'],
        rows: data.projectComparison.map((r) => [
          r.projectName, r.verdictBar.scale, r.verdictBar.fix, r.verdictBar.kill,
          r.primaryKpi.label, r.primaryKpi.value, r.primaryKpi.trend,
        ]),
      });
    }

    // KPI summary
    if (data.kpiSummary?.length) {
      sections.push({
        title: 'Performance KPIs',
        headers: ['KPI', 'Value', 'Unit', 'Delta', 'Percentile', 'Primary'],
        rows: data.kpiSummary.map((k) => [
          k.label, k.value, k.unit, k.delta, k.percentile, k.isPrimary ? 'Yes' : 'No',
        ]),
      });
    }

    downloadMultiSectionCsv(sections, 'verdicts-tab');
  }

  return (
    <>
      {/* Tab-level combined export */}
      <div className="flex justify-end mb-2">
        <Button variant="secondary" size="sm" onClick={handleExportAll}>
          <Download size={14} />
          Export CSV
        </Button>
      </div>

      <div className="tab-csv-consolidated space-y-4">
        {/* Verdict Cards (Scale/Fix/Kill) with multi-expand */}
        <VerdictCards />

        {/* Overnight verdict changes strip (Phase 29) */}
        <VerdictChangesStrip changes={data?.verdictChanges} />

        {/* Project Comparison (only when all projects selected) */}
        <ProjectComparison />

        {/* Verdict Trend Chart */}
        <VerdictTrendChart />

        {/* Stripe-style enrichment row */}
        <div className="grid grid-cols-3 gap-3">
          <TopBottomPerformers />
          <VerdictByPlacement />
          <KeyMovements />
        </div>

        {/* KPI Summary Row */}
        <KpiSummaryRow />
      </div>
    </>
  );
}

// ============================================================================
// QUALITY GATES CONTENT
// ============================================================================

function QualityGatesContent() {
  const { filters } = useDashboardContext();
  useVerdictSummary(filters);
  const { data: gateData } = useGateSummary(filters);

  function handleExportAll() {
    if (!gateData) return;
    const sections: Parameters<typeof downloadMultiSectionCsv>[0] = [];

    // Gate pass rates
    if (gateData.gates?.length) {
      sections.push({
        title: 'Gate Pass Rates',
        headers: ['Gate', 'Role', 'Pass Rate', 'Pass Count', 'Fail Count', 'Trend'],
        rows: gateData.gates.map((g) => [
          g.name, g.role, g.passRate, g.passCount, g.failCount, g.trend,
        ]),
      });
    }

    // Heatmap
    if (gateData.heatmapData?.rows?.length) {
      const cols = gateData.heatmapData.columns;
      sections.push({
        title: 'Segment Heatmap',
        headers: ['Gate', ...cols],
        rows: gateData.heatmapData.rows.map((r) => [
          r.gateName,
          ...cols.map((col) => r.cells[col] ?? 0),
        ]),
      });
    }

    downloadMultiSectionCsv(sections, 'gates-tab');
  }

  return (
    <>
      <div className="flex justify-end mb-2">
        <Button variant="secondary" size="sm" onClick={handleExportAll}>
          <Download size={14} />
          Export CSV
        </Button>
      </div>
      <div className="tab-csv-consolidated space-y-4">
        <GateBars />
        <SegmentHeatmap />
      </div>
    </>
  );
}

// ============================================================================
// REPORTS CONTENT
// ============================================================================

function ReportsContent() {
  const [view, setView] = useState<'list' | 'builder'>('list');
  const [editReport, setEditReport] = useState<import('@/types/dashboard').SavedReport | undefined>();

  if (view === 'builder') {
    return (
      <ReportBuilder
        existingReport={editReport}
        onBack={() => { setView('list'); setEditReport(undefined); }}
      />
    );
  }

  return (
    <ReportList
      onNewReport={() => { setEditReport(undefined); setView('builder'); }}
      onOpenReport={(report) => { setEditReport(report); setView('builder'); }}
    />
  );
}

// ============================================================================
// INSIGHTS CONTENT
// ============================================================================

const INSIGHTS_VIEWS: Record<InsightsSubView, React.FC> = {
  drivers: KpiDrivers,
  composition: PortfolioComposition,
  calibration: TemplateCalibration,
  explorer: CustomExplorer,
};

function InsightsContent() {
  const { filters } = useDashboardContext();
  const activeView = filters.insightsSubView ?? 'drivers';
  const ActiveComponent = INSIGHTS_VIEWS[activeView];

  return (
    <>
      <InsightsNav />
      <ActiveComponent />
    </>
  );
}

// ============================================================================
// MAIN EXPORT (with Provider wrapper)
// ============================================================================

export function AnalyticsDashboardPage() {
  return (
    <DashboardProvider>
      <DashboardInner />
    </DashboardProvider>
  );
}

export default AnalyticsDashboardPage;
